/**
 * SurveyFill — the built-in Communications survey, rendered for a respondent.
 *
 * Single implementation of loading + question rendering + skip-logic +
 * submission against the public survey endpoints:
 *   GET  /api/surveys/public/{id}
 *   POST /api/surveys/responses
 *
 * Used by:
 *  - PublicSurveyPage (the distribution-link page) with page chrome
 *  - the LMS course players (embedded — a lesson references a survey_id), which
 *    pass `embedded` + `memberId` and get `onCompleted` when the learner submits.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { CheckCircle, Loader2, AlertTriangle } from 'lucide-react';
import { getWeaveinApiUrl } from '@/src/config';

interface SkipCondition {
  question_id?: string;
  questionId?: string;
  operator:
    | 'equals'
    | 'not_equals'
    | 'contains'
    | 'not_contains'
    | 'in'
    | 'is_populated'
    | 'is_empty'
    | 'gt'
    | 'lt'
    | 'gte'
    | 'lte';
  value?: unknown;
}
type ShowIf = SkipCondition | { all: SkipCondition[] } | { any: SkipCondition[] };
interface SkipLogic {
  show_if?: ShowIf;
}
export interface SurveyQuestion {
  id: string;
  survey_id: string;
  question_text: string;
  question_type: string;
  options: string[] | null;
  required: boolean;
  order: number;
  validation_rules: Record<string, unknown> | null;
  skip_logic: SkipLogic | null;
}
export interface PublicSurvey {
  id: string;
  name: string;
  description?: string | null;
  status: string;
  collect_anonymous: boolean;
  default_language: string;
  supported_languages: string[];
  questions: SurveyQuestion[];
}
type AnswerValue = string | number | boolean | null;
type AnswersMap = Record<string, AnswerValue>;

const evalCondition = (c: SkipCondition, answers: AnswersMap): boolean => {
  const qid = c.question_id ?? c.questionId ?? '';
  const a = answers[qid];
  const has = a !== undefined && a !== null && a !== '';
  switch (c.operator) {
    case 'is_populated':
      return has;
    case 'is_empty':
      return !has;
    case 'equals':
      return String(a ?? '').toLowerCase() === String(c.value ?? '').toLowerCase();
    case 'not_equals':
      return String(a ?? '').toLowerCase() !== String(c.value ?? '').toLowerCase();
    case 'contains':
      return has && String(a).toLowerCase().includes(String(c.value ?? '').toLowerCase());
    case 'not_contains':
      return !has || !String(a).toLowerCase().includes(String(c.value ?? '').toLowerCase());
    case 'in':
      return Array.isArray(c.value) && c.value.map((v) => String(v).toLowerCase()).includes(String(a ?? '').toLowerCase());
    case 'gt':
      return has && Number(a) > Number(c.value);
    case 'lt':
      return has && Number(a) < Number(c.value);
    case 'gte':
      return has && Number(a) >= Number(c.value);
    case 'lte':
      return has && Number(a) <= Number(c.value);
    default:
      return true;
  }
};

const isQuestionVisible = (q: SurveyQuestion, answers: AnswersMap): boolean => {
  const sl = q.skip_logic;
  if (!sl) return true;
  const showIf: ShowIf | undefined =
    sl.show_if ?? ((sl as any).operator ? (sl as unknown as SkipCondition) : undefined);
  if (!showIf) return true;
  if ('all' in showIf) return showIf.all.every((c) => evalCondition(c, answers));
  if ('any' in showIf) return showIf.any.some((c) => evalCondition(c, answers));
  return evalCondition(showIf as SkipCondition, answers);
};

interface Props {
  surveyId: string;
  memberId?: string | null;
  embedded?: boolean; // no full-page chrome; compact thank-you
  onCompleted?: () => void; // fired after a successful submit
}

export const SurveyFill: React.FC<Props> = ({ surveyId, memberId = null, embedded = false, onCompleted }) => {
  const [survey, setSurvey] = useState<PublicSurvey | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [answers, setAnswers] = useState<AnswersMap>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const r = await fetch(getWeaveinApiUrl(`/api/surveys/public/${surveyId}`));
        if (cancelled) return;
        if (r.status === 404) {
          setLoadError('This survey is no longer available.');
          return;
        }
        if (!r.ok) {
          setLoadError(`Could not load survey (${r.status}).`);
          return;
        }
        setSurvey(await r.json());
      } catch {
        if (!cancelled) setLoadError('Network error loading the survey.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [surveyId]);

  const setAnswer = (qid: string, value: AnswerValue) => setAnswers((prev) => ({ ...prev, [qid]: value }));

  const visibleQuestions = useMemo(
    () => (survey ? survey.questions.filter((q) => isQuestionVisible(q, answers)) : []),
    [survey, answers],
  );

  const validate = (): string | null => {
    if (!survey) return 'Survey not loaded yet.';
    for (const q of visibleQuestions) {
      if (!q.required) continue;
      const a = answers[q.id];
      if (a === undefined || a === null || a === '') return `"${q.question_text}" is required.`;
    }
    if (!memberId && !survey.collect_anonymous) return 'This survey requires a member link.';
    return null;
  };

  const handleSubmit = async () => {
    const v = validate();
    if (v) {
      setSubmitError(v);
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    const visibleIds = new Set(visibleQuestions.map((q) => q.id));
    const submittedAnswers: AnswersMap = Object.fromEntries(
      Object.entries(answers).filter(([qid]) => visibleIds.has(qid)),
    );
    try {
      const r = await fetch(getWeaveinApiUrl('/api/surveys/responses'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ survey_id: surveyId, member_id: memberId || undefined, answers: submittedAnswers }),
      });
      if (!r.ok) {
        const body = await r.text();
        setSubmitError(`Submission failed (${r.status}). ${body.slice(0, 200)}`);
        return;
      }
      setSubmitted(true);
      onCompleted?.();
    } catch {
      setSubmitError('Network error submitting your response.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className={embedded ? 'py-10 flex justify-center' : 'min-h-screen bg-gray-50 flex items-center justify-center'}>
        <Loader2 className="w-6 h-6 animate-spin text-gray-500" />
      </div>
    );
  }

  if (loadError) {
    const body = (
      <Card className="max-w-md w-full">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-amber-700">
            <AlertTriangle className="w-5 h-5" /> Survey unavailable
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-gray-700">{loadError}</p>
        </CardContent>
      </Card>
    );
    return embedded ? <div className="py-6">{body}</div> : (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">{body}</div>
    );
  }

  if (submitted) {
    if (embedded) {
      return (
        <div className="py-8 text-center">
          <CheckCircle className="w-8 h-8 text-green-600 mx-auto mb-2" />
          <p className="text-sm text-gray-700">Thank you — your responses have been recorded.</p>
        </div>
      );
    }
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-green-700">
              <CheckCircle className="w-5 h-5" /> Thank you
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-700">Your responses have been recorded.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!survey) return null;

  const questions = (
    <>
      {visibleQuestions.map((q, idx) => (
        <Card key={q.id}>
          <CardContent className="pt-6 space-y-3">
            <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
              {idx + 1}. {q.question_text}
              {q.required ? <span className="text-red-500 ml-1">*</span> : null}
            </div>
            <QuestionInput question={q} value={answers[q.id]} onChange={(v) => setAnswer(q.id, v)} />
          </CardContent>
        </Card>
      ))}
      {submitError ? <p className="text-sm text-red-700">{submitError}</p> : null}
      <div className="flex justify-end">
        <Button onClick={handleSubmit} disabled={submitting} className="min-w-[120px]">
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Submit'}
        </Button>
      </div>
    </>
  );

  if (embedded) {
    return (
      <div className="space-y-4">
        {survey.description ? <p className="text-sm text-gray-600 dark:text-gray-300">{survey.description}</p> : null}
        {questions}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-2xl mx-auto space-y-4">
        <Card>
          <CardHeader>
            <CardTitle>{survey.name}</CardTitle>
            {survey.description ? <p className="text-sm text-gray-600 mt-1">{survey.description}</p> : null}
          </CardHeader>
        </Card>
        {questions}
      </div>
    </div>
  );
};

const QuestionInput: React.FC<{
  question: SurveyQuestion;
  value: AnswerValue;
  onChange: (v: AnswerValue) => void;
}> = ({ question, value, onChange }) => {
  const t = question.question_type;

  if (t === 'yes_no') {
    return (
      <div className="flex gap-2">
        {['yes', 'no'].map((opt) => (
          <Button key={opt} type="button" variant={value === opt ? 'default' : 'outline'} onClick={() => onChange(opt)} className="capitalize">
            {opt}
          </Button>
        ))}
      </div>
    );
  }
  if (t === 'multiple_choice' && question.options) {
    return (
      <div className="flex flex-col gap-2">
        {question.options.map((opt) => (
          <label key={opt} className="flex items-center gap-2 cursor-pointer">
            <input type="radio" name={question.id} value={opt} checked={value === opt} onChange={() => onChange(opt)} />
            <span className="text-sm text-gray-800 dark:text-gray-200">{opt}</span>
          </label>
        ))}
      </div>
    );
  }
  if (t === 'scale' || t === 'rating') {
    const min = (question.validation_rules?.min as number) ?? 1;
    const max = (question.validation_rules?.max as number) ?? 5;
    const ticks = [];
    for (let i = min; i <= max; i++) ticks.push(i);
    return (
      <div className="flex gap-2 flex-wrap">
        {ticks.map((n) => (
          <Button key={n} type="button" variant={value === n ? 'default' : 'outline'} onClick={() => onChange(n)} className="w-10 h-10 p-0">
            {n}
          </Button>
        ))}
      </div>
    );
  }
  if (t === 'nps') {
    const ticks = Array.from({ length: 11 }, (_, i) => i);
    return (
      <div className="flex gap-1 flex-wrap">
        {ticks.map((n) => (
          <Button key={n} type="button" variant={value === n ? 'default' : 'outline'} onClick={() => onChange(n)} className="w-9 h-9 p-0 text-xs">
            {n}
          </Button>
        ))}
      </div>
    );
  }
  if (t === 'number') {
    return (
      <Input
        type="number"
        value={(value as number | null) ?? ''}
        onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
        placeholder="Enter a number"
      />
    );
  }
  return (
    <Input type="text" value={(value as string | null) ?? ''} onChange={(e) => onChange(e.target.value)} placeholder="Your answer" />
  );
};

export default SurveyFill;
